"use client";

import {
  useCallback,
  useMemo,
  useRef,
  useState,
  CSSProperties,
  useEffect,
} from "react";
import { toast } from "react-toastify";

import { getMask } from "@/app/_actions/getMask";
import { ETool, useAutoMaskContext, useUndressContext } from "@/providers";
import { createFile } from "@/utils/converter/createFile";
import { getImageSize } from "@/utils/converter/getImageSize";

import { DrawingEvent } from "./types";
import { INITIAL_COORDINATES } from "./vars";

/**
 * Хук для управления рисованием маски на canvas
 *
 * @description
 * Предоставляет функционал для:
 * - Рисования и стирания маски кистью разного размера
 * - Отмены последнего действия (undo)
 * - Полной очистки маски
 * - Автоматического создания маски через AI
 * - Управления курсором и его размером
 * - Поддержки как мыши, так и тач-событий
 *
 * @param {string} id - Идентификатор файла для синхронизации с глобальным состоянием
 * @param {string} originalImage - URL или base64 строка исходного изображения
 *
 * @returns {Object} Объект с методами и свойствами:
 * - canvasRef: React.RefObject<HTMLCanvasElement> - реф для canvas элемента
 * - handlers: Object - обработчики событий для canvas
 * - cursorProps: Object - свойства для отображения курсора
 *
 * @example
 * ```tsx
 * const { canvasRef, handlers, cursorProps } = useDrawing(id, imageUrl);
 *
 * return (
 *   <div>
 *     <canvas ref={canvasRef} {...handlers} />
 *     <Cursor {...cursorProps} />
 *   </div>
 * );
 * ```
 */
export const useDrawing = (id: string, originalImage: string) => {
  // Контексты для доступа к глобальному состоянию
  const { clickFileId, setUndressingsFiles } = useUndressContext();
  const {
    isShowDraw,
    brushSize,
    currentTool,
    setTool,
    isUndoDraw,
    setUndoDraw,
    isCleanDraw,
    setCleanDraw,
    startLoading,
    stopLoading,
  } = useAutoMaskContext();

  // Состояния для управления canvas
  const [historyDraw, setHistoryDraw] = useState<ImageData[]>([]);
  const [originalContext, setOriginalContext] =
    useState<CanvasRenderingContext2D | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Состояния для управления рисованием
  const [requestId, setRequestId] = useState<number | null>(null);
  const [lastX, setLastX] = useState<number>(0);
  const [lastY, setLastY] = useState<number>(0);
  const [drawing, setDrawing] = useState(false);

  // Состояния для управления курсором
  const [cursorPosition, setCursorPosition] = useState(INITIAL_COORDINATES);
  const [isCenterCursor, setCenterCursor] = useState(true);

  /**
   * Вычисляем размер курсора на основе размера canvas и установленного размера кисти
   */
  const cursorSize = useMemo(
    () => ((canvasRef.current?.clientWidth ?? 100) * brushSize) / 100,

    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brushSize, originalContext, isShowDraw],
  );

  /**
   * Формируем свойства для курсора (размер, позиция, анимация)
   */
  const cursorProps = useMemo(
    () => ({
      isErase: currentTool === ETool.RUBBER,
      style: {
        width: cursorSize,
        height: cursorSize,
        transform: `translate(${cursorPosition.x}px, ${cursorPosition.y}px)`,
        transitionDuration: isCenterCursor ? "200ms" : "0ms",
      } as CSSProperties,
    }),
    [
      currentTool,
      cursorPosition.x,
      cursorPosition.y,
      cursorSize,
      isCenterCursor,
    ],
  );

  /**
   * Получаем координаты центра canvas для позиционирования курсора
   */
  const getCenterCanvas = useCallback(() => {
    if (!canvasRef.current) {
      return INITIAL_COORDINATES;
    }

    const canvasRect = canvasRef.current.getBoundingClientRect();
    return {
      x: canvasRect.left + canvasRect.width / 2,
      y: canvasRect.top + canvasRect.height / 2,
    };
  }, []);

  /**
   * Обновляем маску в глобальном состоянии
   */
  const updateUndressMask = useCallback(
    (remove?: boolean) => {
      if (!canvasRef.current) {
        return;
      }

      setUndressingsFiles((prevFiles) =>
        prevFiles.map((file) => {
          if (file.id === id) {
            return {
              ...file,
              mask: remove ? null : canvasRef.current,
            };
          }
          return file;
        }),
      );
    },
    [setUndressingsFiles, id],
  );

  /**
   * Обрабатываем изображение и настраиваем canvas
   */
  const setupCanvas = useCallback(
    async (image: string, $ctx?: CanvasRenderingContext2D) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }

      try {
        const ctx =
          $ctx ??
          canvas.getContext("2d", {
            willReadFrequently: true,
          });
        if (!ctx) {
          return;
        }

        const fileSize = await getImageSize(image);

        // Устанавливаем размеры canvas
        canvas.width = fileSize.width;
        canvas.height = fileSize.height;

        // Очищаем canvas
        ctx.clearRect(0, 0, fileSize.width, fileSize.height);

        // Инициализируем историю
        const imageData = ctx.getImageData(
          0,
          0,
          fileSize.width,
          fileSize.height,
        );
        setHistoryDraw([imageData]);
      } catch (err) {
        const error = err as Error;
        console.error("Error loading image:", error);
        toast.error(error.message);
      }
    },
    [],
  );

  /**
   * Получаем АвтоМаску от ML
   */
  const getAutoMask = useCallback(async () => {
    try {
      startLoading(id);
      const form = new FormData();

      const image = await createFile(originalImage);

      form.append("image", image);

      const mask = await getMask(form);

      if (mask.type !== "error" && canvasRef.current) {
        const ctx = canvasRef.current.getContext("2d", {
          willReadFrequently: true,
        });

        if (ctx && originalContext) {
          // Мы получаем "чистый" base64 поэтому надо его дополнить
          const imageBase64 = `data:image/jpeg;base64,${mask.data}`;

          await setupCanvas(imageBase64, ctx);

          const image = new Image();
          image.src = imageBase64;

          // Рисуем маску
          ctx.drawImage(image, 0, 0, image.width, image.height);

          // Обновляем маску в контексте
          updateUndressMask();
        }
      }
    } catch (err) {
      const error = err as Error;
      console.error("Error in setAutoMask:", error);
      toast.error(error.message);
      // Запасной вариант, если словили ошибку на этапе автомаски - иначе размер не встанет
      await setupCanvas(originalImage);
    } finally {
      stopLoading(id);
      setTool(ETool.BRUSH);
    }
  }, [
    id,
    originalContext,
    originalImage,
    setTool,
    setupCanvas,
    startLoading,
    stopLoading,
    updateUndressMask,
  ]);

  /**
   * Получаем размер курсора относительно размера canvas
   */
  const getCursorSize = useCallback(
    () => ((canvasRef.current?.width ?? 100) * brushSize) / 100,
    [brushSize],
  );

  /**
   * Получаем координаты курсора для рисования
   */
  const calculateCoordinates = useCallback(
    (event: DrawingEvent) => {
      if (!canvasRef.current) {
        return { draw: INITIAL_COORDINATES, cursor: INITIAL_COORDINATES };
      }

      const canvasRect = canvasRef.current.getBoundingClientRect();
      const { x: centerX, y: centerY } = getCenterCanvas();
      const scaleX = canvasRef.current.width / canvasRect.width;
      const scaleY = canvasRef.current.height / canvasRect.height;

      // Проверяем, является ли событие TouchEvent
      if ("touches" in event) {
        const touch = event.touches[0];

        return {
          draw: {
            x: (touch.pageX - canvasRect.left - window.scrollX) * scaleX,
            y: (touch.pageY - canvasRect.top - window.scrollY) * scaleY,
          },
          cursor: {
            x: touch.clientX - centerX,
            y: touch.clientY - centerY,
          },
        };
      }

      // Для событий мыши
      const mouseEvent = event as React.MouseEvent;

      return {
        draw: {
          x: (mouseEvent.pageX - canvasRect.left - window.scrollX) * scaleX,
          y: (mouseEvent.pageY - canvasRect.top - window.scrollY) * scaleY,
        },
        cursor: {
          x: mouseEvent.clientX - centerX,
          y: mouseEvent.clientY - centerY,
        },
      };
    },
    [getCenterCanvas],
  );

  /**
   * Функци отмены последнего "мазка" рисования
   */
  const undo = useCallback(() => {
    if (historyDraw.length <= 1) {
      return;
    }
    const newStack = [...historyDraw];
    const lastImageData = newStack.pop();

    if (lastImageData && originalContext) {
      setHistoryDraw(newStack);
      originalContext.putImageData(lastImageData, 0, 0);

      /**  Обновляем маску в контексте после undo
       * - если в истории осталась только "один мазок" - удаляем маску из контекста
       */
      updateUndressMask(historyDraw.length <= 2);
    }
  }, [updateUndressMask, historyDraw, originalContext]);

  /**
   * Функция очистки маски
   */
  const clearMask = useCallback(() => {
    if (!originalContext || !canvasRef.current) {
      return;
    }

    // Очищаем весь canvas
    originalContext.clearRect(
      0,
      0,
      canvasRef.current.width,
      canvasRef.current.height,
    );

    // Создаем пустой ImageData для истории
    const emptyImageData = originalContext.getImageData(
      0,
      0,
      canvasRef.current.width,
      canvasRef.current.height,
    );

    // Обновляем историю
    setHistoryDraw([emptyImageData]);

    // Обновляем маску в контексте
    updateUndressMask(true);
  }, [canvasRef, updateUndressMask, originalContext]);

  /**
   * Рисование линии на canvas
   */
  const drawLine = useCallback(
    (x: number, y: number) => {
      if (!originalContext) {
        return;
      }

      // Настраиваем параметры рисования
      originalContext.lineWidth = getCursorSize();
      originalContext.lineCap = "round";

      // Настраиваем режим композиции и цвет
      if (currentTool === "rubber") {
        originalContext.globalCompositeOperation = "destination-out";
      } else {
        originalContext.globalCompositeOperation = "source-over";
        originalContext.strokeStyle = "#eb7100";
      }

      // Рисуем линию
      originalContext.beginPath();
      originalContext.moveTo(lastX, lastY);
      originalContext.lineTo(x, y);
      originalContext.stroke();
    },
    [originalContext, getCursorSize, currentTool, lastX, lastY],
  );

  /**
   * Начало рисования
   */
  const handleStart = useCallback(
    async (event: DrawingEvent) => {
      if (!isShowDraw || !originalContext) {
        return;
      }

      // Сохраняем текущее состояние в историю
      const fileSize = await getImageSize(originalImage);
      const currentImageData = originalContext.getImageData(
        0,
        0,
        fileSize.width,
        fileSize.height,
      );
      setHistoryDraw((prev) => [...prev, currentImageData]);

      // Начинаем рисование
      setDrawing(true);
      const coords = calculateCoordinates(event);
      setLastX(coords.draw.x);
      setLastY(coords.draw.y);
    },
    [isShowDraw, calculateCoordinates, originalContext, originalImage],
  );

  /**
   * Процесс рисования
   */
  const handleMove = useCallback(
    (event: DrawingEvent) => {
      if (!isShowDraw) {
        return;
      }

      const coords = calculateCoordinates(event);

      // Устанавливаем позицию курсора относительно центра
      setCenterCursor(false);
      setCursorPosition({
        x: coords.cursor.x,
        y: coords.cursor.y,
      });

      // Рисуем если активно рисование
      if (drawing && originalContext) {
        setRequestId(
          requestAnimationFrame(() => {
            drawLine(coords.draw.x, coords.draw.y);
            setLastX(coords.draw.x);
            setLastY(coords.draw.y);
          }),
        );
      }
    },
    [isShowDraw, calculateCoordinates, drawing, originalContext, drawLine],
  );

  /**
   * Завершение рисования
   */
  const handleEnd = useCallback(() => {
    if (requestId) {
      cancelAnimationFrame(requestId);
      setRequestId(null);
    }
    setDrawing(false);

    // Обновляем маску в контексте
    updateUndressMask();
  }, [requestId, updateUndressMask]);

  /**
   * Выход за пределы canvas
   */
  const handleLeave = useCallback(() => {
    setCenterCursor(true);
    setCursorPosition(INITIAL_COORDINATES);
    handleEnd();
  }, [handleEnd]);

  /** Инициализация canvas и загрузка изображения */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      return;
    }

    setOriginalContext(ctx);

    // Если есть изображение - настраиваем canvas
    if (originalImage) {
      setupCanvas(originalImage);
    }

    const abortController = new AbortController();

    // Очистка при размонтировании
    return () => {
      abortController.abort();
      setOriginalContext(null);
      setHistoryDraw([]);
    };
  }, [originalImage, getAutoMask, setupCanvas]); // зависим только от изменения изображения

  /**
   *  Объединяем обработку undo и clean в один эффект
   */
  useEffect(() => {
    // Обработка отмены действия
    if (isUndoDraw && id === clickFileId) {
      undo();
      setUndoDraw(false);
    }

    // Обработка очистки
    if (isCleanDraw && id === clickFileId) {
      clearMask();
      setCleanDraw(false);
    }
  }, [
    isUndoDraw,
    isCleanDraw,
    id,
    clickFileId,
    undo,
    clearMask,
    setUndoDraw,
    setCleanDraw,
  ]);

  return {
    canvasRef,
    handlers: {
      onMouseDown: handleStart,
      onMouseMove: handleMove,
      onMouseUp: handleEnd,
      onMouseLeave: handleLeave,
      onTouchStart: handleStart,
      onTouchMove: handleMove,
      onTouchEnd: handleLeave,
      style: {
        touchAction: "none",
        aspectRatio:
          (canvasRef.current?.width ?? 1) / (canvasRef.current?.height ?? 1),
      },
    },
    cursorProps,
    getAutoMask,
  };
};
